import { inject, observer } from "mobx-react";
import React from "react";
import RelatedItemContent from "./RelatedItemContent";
import { CategoryService, RelatedItemsService } from "../services";
import { RelatedItemsModel, RelatedSectionModel } from "../models";
import { IOption, SingleSelect } from "@mindmemobile/mm-shared-global";
import { ProductOptionWrapper } from "../styles";

type Props = {
  realtedItemService: RelatedItemsService;
  categoryService?: CategoryService;
  sourceId?: string;
};

type SelectedOptionState = {
  showOptions: boolean;
  description: JSX.Element;
};

@inject("categoryService")
@observer
export default class RelatedItemsSection extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  get relatedSectionData() {
    return this.props.realtedItemService.relatedSectionData;
  }

  get includedSectionState(): SelectedOptionState {
    const { includedItems } = this.relatedSectionData;

    let showOptions = true;
    let description = (
      <>
        This will be included at <b>no cost</b> when this Item is purchased.
      </>
    );
    if (includedItems.showSelectItems) {
      showOptions = false;
      description = (
        <>
          These Items will be <b>included</b> when this Item is purchased.
        </>
      );
    }
    if (includedItems.showAccountCredits) {
      showOptions = false;
      description = (
        <>
          An Account Credit will be issued at <b>no cost</b> when this Item is
          purchased.
        </>
      );
    }
    if (includedItems.showCategories) {
      showOptions = false;
      description = (
        <>
          One Item from the following Categories will be <b>included</b> to
          purchase when this Item is purchased.
        </>
      );
    }
    return {
      showOptions: showOptions,
      description: description,
    } as SelectedOptionState;
  }

  get requiredSectionState(): SelectedOptionState {
    const { requiredItems } = this.relatedSectionData;
    let showOptions = true;
    let description = (
      <>
        This will be <b>required</b> to purchase when this Item is purchased.
      </>
    );
    if (requiredItems.showSelectItems || requiredItems.showSelectItemsPopup) {
      showOptions = false;
      description =
        requiredItems.items.length > 1 ? (
          this.getSelectItemsDescription(requiredItems)
        ) : (
          <>
            These Items will be <b>required</b> when this Item is purchased.
          </>
        );
    }
    if (requiredItems.showCategories) {
      showOptions = false;
      description = (
        <>
          One Item from the following Categories will be <b>required</b> to
          purchase when this Item is purchased.
        </>
      );
    }
    if (requiredItems.showAccountCredits) {
      showOptions = false;
    }
    return {
      showOptions: showOptions,
      description: description,
    } as SelectedOptionState;
  }

  getSelectItemsDescription(requiredItems: RelatedItemsModel): JSX.Element {
    const optionsArray: IOption[] = requiredItems.items
      .slice(0, -1)
      .map((_, index) => ({
        label: `${index + 1}`,
        value: `${index + 1}`,
      }));
    optionsArray.push({
      label: "All",
      value: `${requiredItems.items.length}`,
    });
    const options = [optionsArray];
    return (
      <>
        These Items will be <b>required</b> when this Item is purchased. Require
        <SingleSelect
          selectedKey={requiredItems.totalRequired.toString()}
          searchable={false}
          nameKeys={["label"]}
          valueKeys={["value"]}
          options={options || []}
          onChange={(option: any) => this.handleSelectChange(option.value)}
          className="dpdn-no-border text-royal-blue related-item-single-select"
          width="auto"
        />
        {requiredItems.totalRequired > 1 ? "Items" : "Item"}
      </>
    );
  }

  handleSelectChange(value: string) {
    const newValue = Number(value);
    this.props.realtedItemService.update(
      new RelatedSectionModel({
        ...this.relatedSectionData,
        requiredItems: new RelatedItemsModel({
          ...this.relatedSectionData.requiredItems,
          totalRequired: newValue,
        }),
      })
    );
  }

  get recommendedSectionState(): SelectedOptionState {
    const { recommendedItems } = this.relatedSectionData;

    let showOptions = true;
    let description = (
      <>
        This will be <b>recommended</b> when this Item is purchased.
      </>
    );
    if (
      recommendedItems.showSelectItems ||
      recommendedItems.showSelectItemsPopup
    ) {
      showOptions = false;
      description = (
        <>
          These Items will be <b>recommended</b> when this Item is purchased
        </>
      );
    }
    if (recommendedItems.showCategories) {
      showOptions = false;
      description = (
        <>
          One Item from the following Categories will be <b>required</b> to
          purchase when this Item is purchased.
        </>
      );
    }

    if (recommendedItems.showAccountCredits) {
      showOptions = false;
    }
    return {
      showOptions: showOptions,
      description: description,
    } as SelectedOptionState;
  }

  get includedSection(): React.ReactNode {
    const { includedItems } = this.relatedSectionData;
    return (
      <RelatedItemContent
        sourceId={this.props.sourceId}
        item={includedItems}
        multiplePriceSelection={false}
        label="Included"
        description={this.includedSectionState.description}
        showOptions={this.includedSectionState.showOptions}
        onUpdate={(data: RelatedItemsModel) =>
          this.props.realtedItemService.update(
            new RelatedSectionModel({
              ...this.relatedSectionData,
              includedItems: new RelatedItemsModel(data),
            })
          )
        }
        reltedItemService={this.props.realtedItemService}
        categoryService={this.props.categoryService}
        hideIssueCredit={true}
      />
    );
  }

  get requiredSection(): React.ReactNode {
    const { requiredItems } = this.relatedSectionData;
    return (
      <RelatedItemContent
        sourceId={this.props.sourceId}
        item={requiredItems}
        label="Required"
        multiplePriceSelection={false}
        description={this.requiredSectionState.description}
        showOptions={this.requiredSectionState.showOptions}
        onUpdate={(data: RelatedItemsModel) =>
          this.props.realtedItemService.update(
            new RelatedSectionModel({
              ...this.relatedSectionData,
              requiredItems: new RelatedItemsModel(data),
            })
          )
        }
        reltedItemService={this.props.realtedItemService}
        categoryService={this.props.categoryService}
        hideIssueCredit={true}
      />
    );
  }

  get recommendedSection(): React.ReactNode {
    const { recommendedItems } = this.relatedSectionData;
    return (
      <RelatedItemContent
        sourceId={this.props.sourceId}
        item={recommendedItems}
        label="Recommended"
        multiplePriceSelection={true}
        description={this.recommendedSectionState.description}
        showOptions={this.recommendedSectionState.showOptions}
        onUpdate={(data: RelatedItemsModel) =>
          this.props.realtedItemService.update(
            new RelatedSectionModel({
              ...this.relatedSectionData,
              recommendedItems: new RelatedItemsModel(data),
            })
          )
        }
        reltedItemService={this.props.realtedItemService}
        categoryService={this.props.categoryService}
        hideIssueCredit={true}
      />
    );
  }

  render() {
    return (
      <ProductOptionWrapper className="related-items-section">
        {this.includedSection}
        {this.requiredSection}
        {this.recommendedSection}
      </ProductOptionWrapper>
    );
  }
}
